import { isChunkUploadFailure } from '@lib/utils/upload-chunk';
import * as Sentry from '@sentry/react';

import { errorMessage, toError } from '@lib/utils/toError';

import type { FileUploadStatus, UploadFailureCategory, UploadPhase } from './UploadManager';

/**
 * Single surface for everything we OBSERVE about uploads: Sentry breadcrumbs,
 * the structured Sentry captures, and the per-file resilience state that later
 * feeds the `Upload Batch Completed` Mixpanel counters.
 *
 * This mirrors how every Mixpanel event lives on `TrackingStore` — call sites
 * read like `telemetry.reportUploadFailed(...)` and the tag / breadcrumb / event
 * vocabulary lives here in ONE place instead of being hand-rolled at each site.
 *
 * Division of labour: the Mixpanel *emit* still lives on `TrackingStore` (per the
 * project's mixpanel-tracking convention). This owns the whole Sentry side, plus
 * the shared resilience accumulator that Mixpanel reads at batch end — a recovered
 * upload throws no error, so that ROI can only be counted here, not inferred from
 * Sentry events.
 */

/** Sentry scope shared by every structured upload capture. */
const UPLOAD_COMPONENT = 'UploadManager';

/** Which resilience mechanism a file leaned on. `retry` = an online transient
 *  error was retried; `offline-park` = the attempt waited for the connection. */
type ResilienceKind = 'retry' | 'offline-park';

/** The upload step a resilience event happened in (breadcrumb prefix). */
type ResiliencePhase = 'chunk' | 'start' | 'finish';

export interface ResilienceCounts {
  retriedFileCount: number;
  offlineParkedFileCount: number;
  finishRecoveredCount: number;
  stalledFileCount: number;
}

/**
 * Network Information API — not in every browser, and untyped in the TS lib.
 * Shared so the failure capture and the stall event describe the link the same
 * way (a stall report is only useful next to what the connection looked like).
 */
export function readNetworkConnection():
  | { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean }
  | undefined {
  return (
    navigator as unknown as {
      connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
    }
  ).connection;
}

export class UploadTelemetry {
  /** Per-upload resilience flags keyed by uuid; drained (read + cleared) at batch
   *  end by `drainBatchResilience`. Non-observable — telemetry only. */
  private resilience = new Map<
    string,
    { retried?: boolean; parked?: boolean; finishRecovered?: boolean; stalled?: boolean }
  >();

  /**
   * A crumb on the `upload` timeline. Breadcrumbs create no standalone event —
   * they attach to the NEXT captured exception, so a later failure report carries
   * the story of what led up to it (parks, retries, self-heals).
   */
  breadcrumb(
    message: string,
    data?: Record<string, unknown>,
    level: 'info' | 'warning' | 'error' = 'info',
  ) {
    Sentry.addBreadcrumb({ category: 'upload', message, data, level });
  }

  /**
   * A file leaned on the resilience layer during a given step. Records the flag
   * for the batch ROI counters AND drops a `warning` breadcrumb so a later failure
   * carries the story.
   */
  resilienceEvent(
    uuid: string,
    kind: ResilienceKind,
    phase: ResiliencePhase,
    data?: Record<string, unknown>,
  ) {
    const firstOfKind = this.mark(uuid, kind === 'retry' ? 'retried' : 'parked');
    // Only breadcrumb the FIRST retry/park of each kind per file. A large upload
    // over flaky wifi can fire onRetry/onWait hundreds of times; one crumb per
    // file per kind keeps Sentry's ~100-entry breadcrumb ring from evicting the
    // surrounding story (other files' events, empty-media replace, …) before the
    // eventual failure capture reads it.
    if (firstOfKind) this.breadcrumb(`${phase} ${kind}`, data, 'warning');
  }

  /**
   * These files were mid-transfer when a stall episode began. Unlike retry and
   * offline-park this records an OBSERVATION, not a mechanism the file leaned on —
   * the detector is population-wide (a moving sibling vouches for a quiet one), so
   * the mark means "was in flight during a stall", not "this file was the stuck
   * one". Read at batch end as `stalledFileCount`.
   */
  noteStalled(uuids: Iterable<string>) {
    let firstForAny = false;
    for (const uuid of uuids) {
      if (this.mark(uuid, 'stalled')) firstForAny = true;
    }
    // One crumb per episode, and only when it tells us something new — a link that
    // flaps for an hour would otherwise evict the surrounding story from Sentry's
    // breadcrumb ring, the same reason resilienceEvent breadcrumbs only the first
    // retry/park per file.
    if (firstForAny) this.breadcrumb('transfer stalled', readNetworkConnection(), 'warning');
  }

  /** Set a resilience flag; returns true only the first time it flips false→true. */
  private mark(uuid: string, flag: 'retried' | 'parked' | 'finishRecovered' | 'stalled'): boolean {
    const cur = this.resilience.get(uuid) ?? {};
    if (cur[flag]) return false;
    cur[flag] = true;
    this.resilience.set(uuid, cur);
    return true;
  }

  /**
   * The primary failure funnel: capture a failed file upload to Sentry with rich
   * tags + extra — which stage broke, transfer rate, progress at the moment of
   * failure, network conditions, and error details.
   */
  reportUploadFailed(args: {
    status: FileUploadStatus | undefined;
    uuid: string;
    mediaId: string | null | undefined;
    fileName: string;
    error: unknown;
    failure: { reason: string; category: UploadFailureCategory };
    fallbackPhase: UploadPhase;
  }): void {
    const { status, uuid, mediaId, fileName, error, failure, fallbackPhase } = args;
    const file = status?.file;
    const phase = (error as { uploadPhase?: UploadPhase })?.uploadPhase ?? fallbackPhase;
    const failedChunkIndex = isChunkUploadFailure(error) ? error.chunkIndex : undefined;

    // Windowed transfer rate the UI already tracks (bytes/sec), if we have it.
    const speedBps = status?.smoothedStats?.speedBps;
    const hasSpeed = typeof speedBps === 'number' && Number.isFinite(speedBps);
    const progressPercent = status?.progress;
    const fileSizeBytes = file?.size;
    const uploadedBytesApprox =
      typeof fileSizeBytes === 'number' && typeof progressPercent === 'number'
        ? Math.round((progressPercent / 100) * fileSizeBytes)
        : undefined;

    const connection = readNetworkConnection();
    const errorObj = error instanceof Error ? error : undefined;
    const speedKBps = hasSpeed ? Math.round(speedBps / 1024) : undefined;

    Sentry.captureException(toError(error), {
      tags: {
        component: UPLOAD_COMPONENT,
        action: 'upload_file',
        phase,
        category: failure.category,
      },
      extra: {
        uuid,
        mediaId,
        fileName,
        fileType: file?.type,
        fileSizeBytes,
        libraryId: status?.destination?.libraryId,
        phase,
        reason: failure.reason,
        failedChunkIndex,
        totalParts: status?.uploadFinishData?.partUrls?.length,
        progressPercent,
        uploadedBytesApprox,
        speedBytesPerSec: hasSpeed ? Math.round(speedBps) : undefined,
        speedKBps,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        connectionEffectiveType: connection?.effectiveType,
        connectionDownlinkMbps: connection?.downlink,
        connectionRttMs: connection?.rtt,
        connectionSaveData: connection?.saveData,
        errorName: errorObj?.name,
        errorMessage: errorObj?.message,
        errorCause:
          errorObj && 'cause' in errorObj && errorObj.cause != null
            ? String(errorObj.cause)
            : undefined,
      },
    });
  }

  /**
   * A finish that errored on the client but is confirmed landed server-side (lost
   * ACK / "unlogged pod"). Marks the recovery for the ROI counter and — because a
   * recovered upload throws no error for a breadcrumb to attach to — emits a
   * standalone `info` event so these high-value saves ("user did all the work, we
   * caught a dropped finish response") are visible + countable in Sentry.
   */
  reportFinishRecovered(args: {
    uuid: string;
    mediaId: string;
    fileName: string;
    libraryId: string;
  }) {
    this.mark(args.uuid, 'finishRecovered');
    Sentry.captureMessage('upload finish recovered (server confirmed)', {
      level: 'info',
      tags: { component: UPLOAD_COMPONENT, action: 'upload_file', phase: 'finish' },
      extra: { mediaId: args.mediaId, fileName: args.fileName, libraryId: args.libraryId },
    });
  }

  /**
   * The external-URL import path has no per-file funnel, so total failures were
   * invisible to Sentry. Report the all-failed (non-billing) case as a structured
   * event.
   */
  reportExternalFailed(args: {
    libraryId: string;
    totalBatches: number;
    failedBatches: number;
    failedItems: number;
    error: unknown;
  }) {
    Sentry.captureException(toError(args.error), {
      tags: {
        component: UPLOAD_COMPONENT,
        action: 'upload_external_urls',
        phase: 'external',
      },
      extra: {
        libraryId: args.libraryId,
        totalBatches: args.totalBatches,
        failedBatches: args.failedBatches,
        failedItems: args.failedItems,
        errorMessage: errorMessage(args.error),
      },
    });
  }

  /**
   * A deferred (offline-time) deletion was skipped because the media is no longer
   * an abandoned 'empty' orphan — it advanced to a real state, meaning the upload
   * actually landed server-side and deleting it by id would have destroyed a good
   * file. Surfaced as a standalone warning so these near-misses (a data loss the
   * guard prevented) are visible + countable in Sentry.
   */
  reportDeferredDeleteSkipped(args: {
    mediaId: string;
    libraryId: string;
    status: string | undefined;
  }) {
    this.breadcrumb('deferred delete skipped — media no longer empty', args, 'warning');
    Sentry.captureMessage('deferred media deletion skipped — media no longer empty', {
      level: 'warning',
      tags: { component: UPLOAD_COMPONENT, action: 'deferred_delete' },
      extra: { mediaId: args.mediaId, libraryId: args.libraryId, status: args.status },
    });
  }

  /**
   * Read + clear the resilience flags for a batch's uuids, returning the ROI
   * counts for `Upload Batch Completed`. Draining keeps the map from growing
   * across batches.
   */
  drainBatchResilience(uuids: Iterable<string>): ResilienceCounts {
    let retriedFileCount = 0;
    let offlineParkedFileCount = 0;
    let finishRecoveredCount = 0;
    let stalledFileCount = 0;
    for (const uuid of uuids) {
      const f = this.resilience.get(uuid);
      if (f) {
        if (f.retried) retriedFileCount++;
        if (f.parked) offlineParkedFileCount++;
        if (f.finishRecovered) finishRecoveredCount++;
        if (f.stalled) stalledFileCount++;
      }
      this.resilience.delete(uuid);
    }
    return { retriedFileCount, offlineParkedFileCount, finishRecoveredCount, stalledFileCount };
  }
}
